import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import { useApi } from '../Api';

interface Individual {
  id: number;
  firstName: string;
  lastName: string;
  displayName?: string;
  email: string;
  phoneNumber?: string;
  loginProfileId: number;
  familyId: number;
  familyName: string;
}

interface ClientSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectClient: (loginProfileId: number, familyId: number) => void;
}

export const ClientSearchModal: React.FC<ClientSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectClient,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [loading, setLoading] = useState(false);
  const api = useApi();

  // Search for individuals when search term changes
  useEffect(() => {
    const searchIndividuals = async () => {
      if (!searchTerm.trim() || searchTerm.length < 2) {
        setIndividuals([]);
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(`/api/messaging/SearchMessageableIndividuals?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (response.data?.individuals) {
          setIndividuals(response.data.individuals);
        }
      } catch (error) {
        console.error('Error searching individuals:', error);
        setIndividuals([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(searchIndividuals, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, api]);

  const handleSelectClient = (individual: Individual) => {
    onSelectClient(individual.loginProfileId, individual.familyId);
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose}>
      <div className="w-full max-w-2xl mx-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Start New Conversation</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Search Input */}
          <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Results */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {loading && (
              <div className="text-center py-4 text-gray-500">
                Searching...
              </div>
            )}

            {!loading && searchTerm.length < 2 && (
              <div className="text-center py-4 text-gray-500">
                Enter at least 2 characters to search
              </div>
            )}

            {!loading && searchTerm.length >= 2 && individuals.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No individuals found matching "{searchTerm}"
              </div>
            )}

            {!loading && individuals.map((individual) => (
              <div
                key={`${individual.id}-${individual.familyId}`}
                onClick={() => handleSelectClient(individual)}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-sm">
                          {individual.displayName
                            ? individual.displayName.charAt(0).toUpperCase()
                            : `${individual.firstName.charAt(0)}${individual.lastName.charAt(0)}`}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {individual.displayName || `${individual.firstName} ${individual.lastName}`}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {individual.email}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {individual.familyName}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};